/**
 * Canais de venda (redesign admin, Fase 3): um cartão por canal de origem
 * (`ORDER_ORIGINS`), com pedidos e receita reais de `/api/platform/orders`.
 * Canal sem pedido aparece com zero pedidos (a origem existe; o volume é que é
 * zero) — diferente de fonte ausente, que vira `awaiting_source`.
 */
import { createElement as h, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { PeriodOrdersBoundary } from "../components/PeriodOrdersBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatCount } from "../format.js";
import { isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import { channelBuckets } from "../orders/list-view.js";
import { salesChannelBuckets } from "../orders/channels.js";
import { ORDER_ORIGINS, ORDER_ORIGIN_LABELS } from "../orders/state-machine.js";
import { Card, PageHeader } from "../ui/index.js";
import { AcquisitionReport } from "../components/AcquisitionPanels.js";
import { useAcquisitionContacts, useSalesOrigins } from "../queries.js";
import type { SalesOriginOption } from "../orders/acquisition.js";

export interface CanaisViewProps {
  orders?: OrdersDataset;
  /** Barra de período/atualização já montada. */
  toolbar?: ReactElement;
  /** Cadastro de origens de aquisição (rótulos do relatório por origem). */
  acquisitionOptions?: readonly SalesOriginOption[];
  /** Conversas por origem no período; `null` = sem contagem (conversão fica "—"). */
  contactsByOrigin?: Readonly<Record<string, number>> | null;
}

export function CanaisView({ orders, toolbar, acquisitionOptions, contactsByOrigin }: CanaisViewProps): ReactElement {
  const header = h(PageHeader, { title: "Canais de venda", description: "Pedidos e receita por canal de venda (Mercado Livre, Shopee, Magalu…) e por origem." });

  if (!orders || isOrdersSourceAwaitingContract(orders)) {
    return h(
      "div",
      { className: "page page--canais flex flex-col gap-6", "data-page": "canais" },
      header,
      h(StatusPanel, {
        kind: "awaiting_source",
        title: `Pedidos por canal ${AWAITING_CONTRACT_LABEL}`,
        detail: "A rota /api/platform/orders não respondeu neste ambiente."
      })
    );
  }

  const buckets = channelBuckets(orders.orders);
  const salesChannels = salesChannelBuckets(orders.orders);
  return h(
    "div",
    { className: "page page--canais flex flex-col gap-6", "data-page": "canais" },
    header,
    toolbar ?? null,
    h(AcquisitionReport, { orders: orders.orders, options: acquisitionOptions, contactsByOrigin }),
    h("h2", { className: "text-base font-extrabold text-ink" }, "Por canal de venda"),
    h(
      "div",
      { className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4", "data-sales-channels": String(salesChannels.length) },
      salesChannels.map((bucket) =>
        h(
          Card,
          { key: bucket.key, "data-sales-channel": bucket.key },
          h("h3", { className: "text-sm font-extrabold text-ink" }, bucket.label),
          h("p", { className: "mt-2 text-2xl font-extrabold tabular-nums text-ink" }, formatCount(bucket.count)),
          h("p", { className: "text-xs text-muted" }, "pedido(s)"),
          h("p", { className: "mt-2 text-sm font-bold tabular-nums text-body" }, formatBrl(bucket.revenueCents)),
          bucket.openInBling ? h("p", { className: "text-xs text-muted" }, `${formatCount(bucket.openInBling)} em aberto no Bling`) : null,
          h(Link, { to: `/pedidos?canal=${bucket.key}`, className: "mt-3 inline-block text-sm font-semibold text-brand-text" }, "Ver pedidos"),
          bucket.key === "loja" || bucket.key === "whatsapp" || bucket.key === "manual" || bucket.key === "marketplace" || bucket.key === "bling_manual"
            ? null
            : h(Link, { to: bucket.key === "mercado_livre" ? "/canais/mercado-livre" : `/canais/${bucket.key}`, className: "ml-4 mt-3 inline-block text-sm font-semibold text-brand-text" }, "Abrir quadro")
        )
      )
    ),
    h("h2", { className: "text-base font-extrabold text-ink" }, "Por origem"),
    h(
      "div",
      { className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" },
      ORDER_ORIGINS.map((origin) => {
        const bucket = buckets.find((b) => b.key === origin);
        return h(
          Card,
          { key: origin, "data-channel": origin },
          h("h3", { className: "text-sm font-extrabold text-ink" }, ORDER_ORIGIN_LABELS[origin]),
          h("p", { className: "mt-2 text-2xl font-extrabold tabular-nums text-ink" }, formatCount(bucket?.count ?? 0)),
          h("p", { className: "text-xs text-muted" }, "pedido(s)"),
          h("p", { className: "mt-2 text-sm font-bold tabular-nums text-body" }, formatBrl(bucket?.revenueCents ?? 0)),
          origin === "marketplace"
            ? h(Link, { to: "/canais/mercado-livre", className: "mt-3 inline-block text-sm font-semibold text-brand-text" }, "Abrir quadro do Mercado Livre")
            : null
        );
      })
    )
  );
}

/** Liga a view aos cadastros de origem e à contagem de contatos do período (ambos opcionais: sem eles a view degrada, não quebra). */
function CanaisWithAcquisition({ orders, toolbar, from, to }: { orders: OrdersDataset; toolbar: ReactElement; from: string; to: string }): ReactElement {
  const origins = useSalesOrigins();
  const contacts = useAcquisitionContacts({ from, to });
  return h(CanaisView, { orders, toolbar, acquisitionOptions: origins.data, contactsByOrigin: contacts.data });
}

export function Canais(): ReactElement {
  return h(PeriodOrdersBoundary, {
    defaultPeriod: "mes",
    children: (orders: OrdersDataset, context) => h(CanaisWithAcquisition, { orders, toolbar: context.toolbar, from: context.period.from, to: context.period.to })
  });
}
